import React, { useState } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { Phone, Mail, ArrowUpRight, User } from "lucide-react";
import { api, fileUrl } from "@/lib/api";

const formatPhone = (p) => {
  if (!p) return "";
  const digits = p.replace(/\s+/g, "");
  if (digits.length === 10 && digits.startsWith("07")) {
    return `${digits.slice(0, 3)} ${digits.slice(3, 5)} ${digits.slice(5, 7)} ${digits.slice(7)}`;
  }
  return p;
};

export default function Team() {
  const { t } = useLanguage();
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);

  if (!initialized) {
    setInitialized(true);
    api
      .get("/team")
      .then((res) => setTeam(res.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }

  return (
    <div data-testid="team-page" className="pt-28 md:pt-36">
      <section className="px-6 md:px-12 lg:px-16">
        <div className="text-xs tracking-[0.3em] uppercase text-[#ff5a00] font-mono mb-4">
          / 02 — {t.team.title}
        </div>
        <h1 className="font-display text-6xl sm:text-7xl lg:text-8xl uppercase leading-none">
          {t.team.subtitle}
        </h1>
        <p className="mt-8 max-w-2xl text-zinc-400 text-base md:text-lg">
          {t.team.lead}
        </p>
      </section>

      <section className="px-6 md:px-12 lg:px-16 mt-20">
        {loading ? (
          <div className="text-zinc-500 font-mono text-sm">
            {t.common.loading}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 border-l border-t border-zinc-800">
            {team.map((m) => {
              const imgSrc = m.image_url ? fileUrl(m.image_url) : null;
              return (
                <article
                  key={m.id}
                  data-testid={`team-card-${m.id}`}
                  className="group relative bg-[#0d0d10] border-r border-b border-zinc-800 hover:bg-zinc-900/80 transition-colors"
                >
                  <div className="aspect-[4/5] overflow-hidden bg-zinc-900 flex items-center justify-center">
                    {imgSrc ? (
                      <img
                        src={imgSrc}
                        alt={m.name}
                        className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700 group-hover:scale-105"
                        loading="lazy"
                      />
                    ) : (
                      <div className="flex flex-col items-center justify-center text-zinc-700">
                        <User size={64} strokeWidth={1.2} />
                        <div className="mt-3 text-[10px] font-mono tracking-[0.3em] uppercase">
                          Bild kommer
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="p-8 border-t border-zinc-800">
                    <div className="text-xs font-mono tracking-[0.3em] uppercase text-[#ff5a00]">
                      {t.team.roles[m.role_key] || m.role_key}
                    </div>
                    <h3 className="mt-3 font-display text-3xl md:text-4xl uppercase tracking-wide leading-tight">
                      {m.name}
                    </h3>

                    <div className="mt-8 space-y-3">
                      <a
                        href={`tel:${m.phone}`}
                        data-testid={`team-${m.id}-phone`}
                        className="flex items-center justify-between gap-3 text-sm border-b border-zinc-800 pb-3 hover:border-[#ff5a00] transition-colors group/btn"
                      >
                        <span className="flex items-center gap-3 text-zinc-300">
                          <Phone size={14} className="text-[#ff5a00]" />
                          {formatPhone(m.phone)}
                        </span>
                        <ArrowUpRight
                          size={14}
                          className="text-zinc-600 group-hover/btn:text-[#ff5a00] transition-colors"
                        />
                      </a>
                      <a
                        href={`mailto:${m.email}`}
                        data-testid={`team-${m.id}-email`}
                        className="flex items-center justify-between gap-3 text-sm border-b border-zinc-800 pb-3 hover:border-[#ff5a00] transition-colors group/btn"
                      >
                        <span className="flex items-center gap-3 text-zinc-300 break-all">
                          <Mail size={14} className="text-[#ff5a00] shrink-0" />
                          {m.email}
                        </span>
                        <ArrowUpRight
                          size={14}
                          className="text-zinc-600 group-hover/btn:text-[#ff5a00] transition-colors shrink-0"
                        />
                      </a>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
